import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, mergeProps as _mergeProps, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "price-health-value" }
const _hoisted_2 = { class: "vp-popover price-health-popover" }
const _hoisted_3 = { class: "price-health-verdict" }
const _hoisted_4 = { class: "vp-popover-row" }
const _hoisted_5 = { class: "vp-popover-value" }
const _hoisted_6 = { class: "vp-popover-row" }
const _hoisted_7 = { class: "vp-popover-value" }
const _hoisted_8 = { class: "vp-popover-row" }
const _hoisted_9 = { class: "vp-popover-row" }
const _hoisted_10 = { class: "vp-popover-value" }
const _hoisted_11 = { class: "vp-popover-row" }
const _hoisted_12 = { class: "vp-popover-value" }
const _hoisted_13 = {
  key: 1,
  class: "vp-popover-row"
}
const _hoisted_14 = { class: "vp-popover-value" }
const _hoisted_15 = {
  key: 2,
  class: "vp-popover-note"
}

import { computed } from 'vue';
import { readPriceHealth, PERCENT_DECIMALS, type PriceHealth } from '@/utils/priceHealth';
import { erpStatusLabel } from '@/utils/erpPricingStatus';
import { useCompactTables } from '@/composables/useCompactTables';

const CLAMP_PERCENT = 999;


export default /*@__PURE__*/_defineComponent({
  __name: 'PriceHealth',
  props: {
    params: {}
  },
  setup(__props: any) {

const props = __props;

// The column holds the sortable percent; the detail comes off the raw block on
// the row, so the two can never disagree about which order they describe.
const reading = computed(() => readPriceHealth(props.params.data?.priceHealth));

// Odoo's label, never the raw code; '' when the backend sent no state, which
// hides the row.
const odooStatus = computed(() => erpStatusLabel(props.params.data?.erpPricingStatus));

const tone = computed(() => {
  const { state, direction, odooOnlyLines, engineOnlyLines } = reading.value;
  if (state !== 'measured') return 'muted';
  // Under our own number is bad whatever the coverage: the lines we can see
  // are already quoted short, and the ones we cannot see will not undo that.
  if (direction === 'down') return 'bad';
  // At or above our number, but some priced line sits outside the comparison,
  // so the green would be claiming more than was measured. Amber reads as
  // "nothing wrong in what we checked, and we did not check all of it".
  if (odooOnlyLines + engineOnlyLines > 0) return 'warn';
  // Level counts as good on purpose: matching our own number is the target,
  // not a near miss.
  return 'good';
});

// A phone shows the number alone. The column is the narrowest thing in the row
// and an arrow is a third of its content, while the colour has already said
// which way the drift runs.
const compactTables = useCompactTables();

const icon = computed(() => {
  switch (reading.value.direction) {
    case 'up':
      return compactTables.value ? '' : 'mdi-arrow-up';
    case 'down':
      return compactTables.value ? '' : 'mdi-arrow-down';
    // The one glyph a phone keeps: green "0.0%" is a number that happens to be
    // zero, where "= 0.0%" is the two sides agreeing, which is the point.
    case 'level':
      return 'mdi-equal';
    // Nothing measured: the em dash below carries the cell on its own, and an
    // icon beside it would only look like a reading that failed to load.
    default:
      return '';
  }
});

// Old quotations with placeholder prices ($0.01 engine against a real Odoo
// line) produce percentages in the millions, which would widen the column for
// one junk row. The number is still exact in the popover; only the cell clamps.
const label = computed(() => {
  const percent = reading.value.driftPercent;
  if (percent === null) return '—';
  const magnitude = Math.abs(percent);
  if (magnitude > CLAMP_PERCENT) return `>${CLAMP_PERCENT}%`;
  return `${magnitude.toFixed(PERCENT_DECIMALS)}%`;
});

/** Always the exact figure, for the popover, where there is room for it. */
const exactLabel = computed(() => {
  const percent = reading.value.driftPercent;
  return percent === null ? '—' : `${Math.abs(percent).toFixed(PERCENT_DECIMALS)}%`;
});

const money = (value: number): string =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const signedMoney = (value: number): string => `${value > 0 ? '+' : value < 0 ? '-' : ''}${money(Math.abs(value))}`;

const lineCount = (count: number): string => `${count} line${count === 1 ? '' : 's'}`;

/**
 * Coverage as one fraction: how many of the order's lines the figures above
 * actually looked at. It replaced "7 compared · PE 9 · Odoo 7", where the
 * reader had to do the subtraction themselves; the side a missing line is
 * missing from is in the note below.
 *
 * The denominator is every line carrying a price on one side or both, which is
 * the most that could ever have been compared. Deliberately NOT the order's
 * whole line count: freight and delivery lines are lines of the order that the
 * engine never prices, so they would sit in the denominator forever and make
 * every order look half measured. The word "priced" is kept out of the wording
 * because everywhere else in this panel it means priced on BOTH sides, which
 * is the numerator here, not the denominator.
 */
const comparedLabel = computed(() => {
  const { odooOnlyLines, engineOnlyLines, raw } = reading.value;
  const compared = raw?.comparedLines ?? 0;
  const total = compared + odooOnlyLines + engineOnlyLines;

  return `${compared} out of ${total} order line${total === 1 ? '' : 's'}`;
});

/**
 * The coverage caveat under the figures: which priced lines the percentage
 * above did NOT look at. It is what the amber cell means, and it is shown on a
 * red cell too, where the drift is just as partial.
 */
const uncoveredNote = computed(() => {
  const { odooOnlyLines, engineOnlyLines } = reading.value;
  const total = odooOnlyLines + engineOnlyLines;
  if (total === 0) return '';

  const sides: string[] = [];
  if (odooOnlyLines) sides.push(`${lineCount(odooOnlyLines)} priced in Odoo only`);
  if (engineOnlyLines) sides.push(`${lineCount(engineOnlyLines)} priced in the engine only`);

  return `${sides.join(' and ')} ${total === 1 ? 'is' : 'are'} outside this reading.`;
});

/**
 * The second half of an amber verdict. Without it the headline reads as a clean
 * bill of health in the one colour that is not giving one: "Odoo matches the
 * engine price." over an amber cell is a contradiction until it says how much
 * of the order it is talking about.
 *
 * "Missing a price" covers both directions, because each uncovered line is a
 * line one of the two sides has not priced. Which side is in the note below.
 */
const missingPrice = computed(() => {
  const total = reading.value.odooOnlyLines + reading.value.engineOnlyLines;
  return `${lineCount(total)} ${total === 1 ? 'is' : 'are'} missing a price.`;
});

const verdict = computed(() => {
  const { state, driftPercent } = reading.value;
  if (state === 'no-lines') {
    return 'This order has no active lines, so there is nothing to measure.';
  }
  if (state === 'nothing-to-compare') {
    return 'No line is priced in both the engine and Odoo yet, so there is nothing to compare.';
  }
  // Separate from the one above, because the line counts in this panel would
  // flatly contradict a verdict claiming nothing is priced on both sides.
  if (state === 'no-benchmark') {
    return 'The lines priced on both sides come to $0.00 on our side, so there is no benchmark to measure a difference against.';
  }

  const headline =
    driftPercent === 0
      ? 'Odoo matches the engine price.'
      : driftPercent !== null && driftPercent > 0
      ? `Odoo holds ${exactLabel.value} more than the engine price.`
      : `Odoo holds ${exactLabel.value} less than the engine price.`;

  // Amber only. A red verdict is already saying something is wrong, so the
  // count would be a second complaint rather than the missing half of the
  // first one; it keeps the note at the bottom like every other reading.
  return tone.value === 'warn' ? `${headline} ${missingPrice.value}` : headline;
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_menu = _resolveComponent("v-menu")!

  return (_openBlock(), _createBlock(_component_v_menu, {
    location: "bottom end",
    offset: 4,
    "open-on-hover": "",
    "open-on-click": "",
    "open-delay": 150,
    "close-delay": 200,
    "close-on-content-click": false
  }, {
    activator: _withCtx(({ props }) => [
      _createElementVNode("span", _mergeProps(props, {
        class: ["price-health", `price-health--${tone.value}`],
        onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
      }), [
        (icon.value)
          ? (_openBlock(), _createBlock(_component_v_icon, {
              key: 0,
              size: "16"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(icon.value), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createElementVNode("span", _hoisted_1, _toDisplayString(label.value), 1)
      ], 16)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, _toDisplayString(verdict.value), 1),
        (reading.value.raw)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("div", _hoisted_4, [
                _cache[1] || (_cache[1] = _createElementVNode("span", { class: "vp-popover-label" }, "Engine", -1)),
                _createElementVNode("span", _hoisted_5, _toDisplayString(money(reading.value.raw.engineAmount)), 1)
              ]),
              _createElementVNode("div", _hoisted_6, [
                _cache[2] || (_cache[2] = _createElementVNode("span", { class: "vp-popover-label" }, "Odoo", -1)),
                _createElementVNode("span", _hoisted_7, _toDisplayString(money(reading.value.raw.odooAmount)), 1)
              ]),
              _createElementVNode("div", _hoisted_8, [
                _cache[3] || (_cache[3] = _createElementVNode("span", { class: "vp-popover-label" }, "Difference", -1)),
                _createElementVNode("span", {
                  class: _normalizeClass(["vp-popover-value", `price-health-text--${tone.value}`])
                }, [
                  _createTextVNode(_toDisplayString(reading.value.driftAmount === null ? '—' : signedMoney(reading.value.driftAmount)) + " ", 1),
                  (reading.value.driftPercent !== null)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode(" (" + _toDisplayString(exactLabel.value) + ")", 1)
                      ], 64))
                    : _createCommentVNode("", true)
                ], 2)
              ]),
              _createElementVNode("div", _hoisted_9, [
                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "vp-popover-label" }, "Compared", -1)),
                _createElementVNode("span", _hoisted_10, _toDisplayString(comparedLabel.value), 1)
              ]),
              _createElementVNode("div", _hoisted_11, [
                _cache[5] || (_cache[5] = _createElementVNode("span", { class: "vp-popover-label" }, "Odoo total", -1)),
                _createElementVNode("span", _hoisted_12, _toDisplayString(money(reading.value.raw.odooTotal)) + " (whole order)", 1)
              ])
            ], 64))
          : _createCommentVNode("", true),
        (odooStatus.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _cache[6] || (_cache[6] = _createElementVNode("span", { class: "vp-popover-label" }, "Odoo status", -1)),
              _createElementVNode("span", _hoisted_14, _toDisplayString(odooStatus.value), 1)
            ]))
          : _createCommentVNode("", true),
        (reading.value.state === 'measured' && reading.value.raw)
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
              _createTextVNode(" Measured over the " + _toDisplayString(reading.value.raw.comparedLines) + " line" + _toDisplayString(reading.value.raw.comparedLines === 1 ? '' : 's') + " priced on both sides, as a share of the engine amount. ", 1),
              (uncoveredNote.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode(_toDisplayString(uncoveredNote.value), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }))
}
}

})